import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { message } from 'antd'
import * as bedFundApi from '@/api/etl'
import { useOrgScopedQueryKey } from '@/hooks/useOrgScopedQueryKey'
import { getApiErrorDetail } from '@/utils/apiError'

export interface BedFundFilters {
  period: string
  page?: number
  page_size?: number
  search?: string
  fil_code?: number
  usl_ok_code?: string
}

export const useBedFundPeriods = () => {
  const orgContextKey = useOrgScopedQueryKey()
  return useQuery({
    queryKey: ['bed-fund-periods', orgContextKey],
    queryFn: () => bedFundApi.getBedFundPeriods(),
  })
}

export const useCheckBedFundPeriod = (period: string | null) => {
  const orgContextKey = useOrgScopedQueryKey()
  return useQuery({
    queryKey: ['bed-fund-period-check', orgContextKey, period],
    queryFn: () => bedFundApi.checkBedFundPeriod(period!),
    enabled: !!period,
  })
}

export const useBedFund = (filters: BedFundFilters) => {
  const orgContextKey = useOrgScopedQueryKey()
  return useQuery({
    queryKey: ['bed-fund', orgContextKey, filters],
    queryFn: () => bedFundApi.getBedFund(filters),
    enabled: !!filters.period,
  })
}

export const useDeleteBedFundPeriod = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (period: string) => bedFundApi.deleteBedFundPeriod(period),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bed-fund'] })
      queryClient.invalidateQueries({ queryKey: ['bed-fund-periods'] })
      message.success('Период удалён')
    },
    onError: (error: unknown) => {
      message.error(getApiErrorDetail(error) || 'Ошибка при удалении')
    },
  })
}

export const useUpdateBedFund = (period: string) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (updates: Record<string, { kf_fact?: number; kf_prikaz?: number }>) =>
      bedFundApi.updateBedFundBatch({ period, updates }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['bed-fund'] })
      queryClient.invalidateQueries({ queryKey: ['bed-fund-periods'] })

      if (data.errors && data.errors.length > 0) {
        message.warning(`Обновлено ${data.updated_count} записей. Ошибок: ${data.errors.length}`)
      } else {
        message.success(`Обновлено ${data.updated_count} записей`)
      }
    },
    onError: (error: unknown) => {
      message.error(getApiErrorDetail(error) || 'Ошибка при обновлении')
    },
  })
}
